import {
  useState, useEffect, useRef, useCallback,
} from 'react';
import * as Sentry from '@sentry/capacitor';
import { useDispatch, useSelector } from 'react-redux';
import { setupIonicReact } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { TextZoom } from '@capacitor/text-zoom';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { useAuthContext } from '../providers/auth-provider';
import {
  updateDeeplinkUrl,
  sessionSelector,
  setInitialLocation,
  setOneTimeScreen,
  handleResetBootstrapToPreUserState,
  setAppStoreUrl,
  clearPendingDeeplinkUrl,
} from '../redux/slices/session';
import { setEmulator, clearEmulator } from '../redux/slices/emulator';
import useAppActivityMonitor from './useAppActivityMonitor';
import {
  getAuthenticatedDeeplinkRoute,
  getUnauthenticatedDeeplinkRoute,
} from '../routes/deeplinks/getDeeplinkRoutes';
import useVersionCompatibility from './useVersionCompatibility';
import useSetOneTimeScreen from './useSetOneTimeScreen';
import { WholeMeClient } from '../pages/wholeme/util/wholeme_client';
import { useUserPermissionsQuery } from '../redux/apis/permissions.api';
import { handleOrientationLock } from '../helpers/screenOrientation';
import { getIsLogoutRedirectToken, decodeToken, storeToken } from '../helpers/tokenHelpers';
import { getAccessTokenOrCookie, setCookie } from '../common/atoms/Login/login.util';

export const BOOTSTRAP_ERROR_STATES = {
  VERSION_ERROR: 'Version Error',
  VERSION_INCOMPATIBLE: 'Version Incompatible',
  ERROR: 'Error',
  PERMISSIONS_ERROR: 'Permissions Error',
};

const applyEdgeToEdgeConfiguration = async () => {
  try {
    if (Capacitor.getPlatform() === 'android') {
      await EdgeToEdge.setBackgroundColor({ color: '#ffffff' });
    }
  } catch (error) {
    console.error('Error applying edge-to-edge configuration:', error);
  }
};

const initializeStatusBarAndEdgeToEdge = async () => {
  await applyEdgeToEdgeConfiguration();
};

const checkTextZoom = async () => {
  const zoomLevel = await TextZoom.getPreferred();
  if (zoomLevel.value !== 1) {
    await TextZoom.set({ value: 1 });
  }
};

const handleIonBackButton = (ev) => {
  ev.detail.register(10, () => {
    // registering event to disable hardware back button/swipe back
  });
};

const handleEmulationError = (error, callback) => {
  console.error('Emulation Session Error: ', error.message);
  callback();
};

export const useBootStrap = () => {
  const {
    user,
    isLoading: isAuthLoading,
    tryAutoLogin,
    isAuthenticated,
    handleUpdateUserLastAccess,
    hasCompletedProfile,
    logout,
    handleRegisterNotifications,
  } = useAuthContext();
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const { deeplinkUrl, initialLocation } = useSelector(sessionSelector);
  const [error, setError] = useState(null);
  const [landingRoute, setLandingRoute] = useState('/');
  const [isBootstrapLoading, setIsBootstrapLoading] = useState(true);
  const [isPostUserBootstrapLoading, setIsPostUserBootstrapLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const location = useLocation();
  const dispatch = useDispatch();
  const checkVersionCompatibility = useVersionCompatibility();
  const getOneTimeScreen = useSetOneTimeScreen();
  const {
    data: permissions,
    isLoading: isLoadingPermissions,
    error: permissionsError,
  } = useUserPermissionsQuery({ user }, { skip: !user, refetchOnMountOrArgChange: true });
  const bootstrapCompleteRef = useRef(null);

  useEffect(() => {
    if (permissionsError) {
      setError(BOOTSTRAP_ERROR_STATES.PERMISSIONS_ERROR);
    }
  }, [permissionsError]);

  const clearLoadingState = () => {
    setIsBootstrapLoading(false);
    setIsPostUserBootstrapLoading(false);
  };

  const handleCompleteBootstrap = () => {
    handleUpdateUserLastAccess();
    setIsBootstrapped(true);
    bootstrapCompleteRef.current = true;
    clearLoadingState();
  };

  const retryBootstrap = () => {
    clearLoadingState();
    setError(null);
    logout();
    dispatch(handleResetBootstrapToPreUserState());
    setRetryCount(retryCount + 1);
  };

  const checkForEmulation = () => {
    try {
      const decodedToken = decodeToken(getAccessTokenOrCookie());
      if (decodedToken?.emulator) {
        dispatch(setEmulator(decodedToken.emulator));
      } else {
        dispatch(clearEmulator());
      }
    } catch (e) {
      console.warn('Emulation Token Check Failed', e);
    }
  };

  const postUserBootstrap = async (_user) => {
    setIsPostUserBootstrapLoading(true);
    // QUESTION: should doPostUserStuff handle error routing from VerifyActivation?

    try {
      Sentry.setUser({
        id: _user.id,
      });
    } catch (e) {
      console.error('Error setting Sentry user', e);
    }

    try {
      await handleRegisterNotifications();
    } catch (e) {
      console.error(e);
    }

    // one time screen
    try {
      const oneTimeScreen = await getOneTimeScreen();
      if (oneTimeScreen) {
        dispatch(setOneTimeScreen(oneTimeScreen));
      }

      // profile completed
      let routeToProfileCompleted = false;
      const wholeMeClient = new WholeMeClient();
      const portraitResult = await wholeMeClient.fetchPortrait();
      if (portraitResult?.portrait?.completed && !hasCompletedProfile) {
        routeToProfileCompleted = true;
      }

      const authenticatedDeeplinkPath = getAuthenticatedDeeplinkRoute({
        viewOneTimeScreen: !!oneTimeScreen,
        deeplinkPath: deeplinkUrl.path,
        viewCompletedProfile: routeToProfileCompleted,
      });

      dispatch(setInitialLocation(authenticatedDeeplinkPath));
    } catch (e) {
      console.error(e);
      setError(BOOTSTRAP_ERROR_STATES.ERROR);
    } finally {
      checkForEmulation();
      setIsPostUserBootstrapLoading(false);
      setIsBootstrapLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      setIsBootstrapLoading(true);
      // Ionic config
      setupIonicReact({
        scrollAssist: true,
        scrollPadding: false,
        swipeBackEnabled: false,
      });

      document.addEventListener('ionBackButton', handleIonBackButton);

      // Capacitor config
      await SplashScreen.hide();

      const handleDeeplinkUrl = (url) => {
        if (url.startsWith('/emulation')) {
          try {
            const [path, token] = url.split('#');
            const [, search] = path.split('?');
            const kParam = new URLSearchParams(search).get('k');

            if (!kParam) {
              throw new Error('Missing required key parameter in URL');
            }

            if (!token) {
              throw new Error('No token found in emulation URL');
            }

            const decodedToken = decodeToken(token);

            if (decodedToken.emulator.user_id && decodedToken.emulator.session_id) {
              if (decodedToken.emulator.session_id !== kParam) {
                throw new Error('Invalid url key');
              }

              storeToken(token);
              setCookie(token);
              dispatch(updateDeeplinkUrl('/'));
            } else {
              throw new Error('Invalid emulator object');
            }
          } catch (e) {
            handleEmulationError(e, logout);
          }
          return;
        }
        dispatch(updateDeeplinkUrl(url));
      };

      if (Capacitor.isNativePlatform()) {
        CapApp.addListener('appUrlOpen', ({ url }) => {
          const u = new URL(url);
          const pathQuery = `${u.pathname}${u.search}`;
          handleDeeplinkUrl(pathQuery);
        });

        initializeStatusBarAndEdgeToEdge();
        checkTextZoom();
        handleOrientationLock();
      } else {
        const pathQuery = `${location.pathname}${location.search}${location.hash}`;
        handleDeeplinkUrl(pathQuery);
      }

      // App config
      if (Capacitor.isNativePlatform()) {
        try {
          const { isCompatible, appStoreUrl } = await checkVersionCompatibility();
          dispatch(setAppStoreUrl(appStoreUrl));
          if (!isCompatible) {
            setError(BOOTSTRAP_ERROR_STATES.VERSION_INCOMPATIBLE);
            setIsBootstrapLoading(false);
            return;
          }
        } catch (e) {
          setError(BOOTSTRAP_ERROR_STATES.VERSION_ERROR);
          setIsBootstrapLoading(false);
          return;
        }
      }

      const didLogin = await tryAutoLogin();
      if (!didLogin) {
        setIsBootstrapLoading(false);
      }
    };

    bootstrap();

    return () => {
      if (Capacitor.isNativePlatform()) {
        CapApp.removeAllListeners();
      }

      document.removeEventListener('ionBackButton', handleIonBackButton);
    };
  }, [retryCount]);

  useEffect(() => {
    const handlePostUserBootstrap = async () => {
      await postUserBootstrap(user);
    };

    if (!isAuthenticated || !user?.id) {
      dispatch(handleResetBootstrapToPreUserState());
      setIsBootstrapped(false);
    } else {
      handlePostUserBootstrap(user);
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (user && initialLocation.path && !isBootstrapped) {
      handleCompleteBootstrap();
    }
  }, [user, initialLocation.path, isBootstrapped, permissions]); // all 'required' bootstrap state should be listed as a dependency here

  const getUnauthenticatedLandingRouteFromDeeplinkPath = useCallback(
    (deeplinkPath) => {
      const context = {
        deeplinkPath,
        redirectToLogin: getIsLogoutRedirectToken(),
      };

      return getUnauthenticatedDeeplinkRoute(context);
    },
    [],
  );

  const getPostBootstrapAuthenticatedDeeplinkLandingRoute = useCallback((deeplinkPath) => {
    const context = {
      deeplinkPath,
      viewOneTimeScreen: false,
    };

    return getAuthenticatedDeeplinkRoute(context);
  }, []);

  useEffect(() => {
    if (deeplinkUrl.path && deeplinkUrl.pending) {
      const unauthenticatedRoute = getUnauthenticatedLandingRouteFromDeeplinkPath(deeplinkUrl.path);
      setLandingRoute(unauthenticatedRoute);

      // only run after bootstrap is complete.  the bootstrap process will capture the initial location
      if (bootstrapCompleteRef.current) {
        const authenticatedRoute = getPostBootstrapAuthenticatedDeeplinkLandingRoute(
          deeplinkUrl.path,
        );
        dispatch(setInitialLocation(authenticatedRoute));
      }
      dispatch(clearPendingDeeplinkUrl());
    }
  }, [
    deeplinkUrl,
    getUnauthenticatedLandingRouteFromDeeplinkPath,
    getPostBootstrapAuthenticatedDeeplinkLandingRoute,
  ]);

  useAppActivityMonitor(user, logout);

  const isLoadingState = isAuthLoading || isPostUserBootstrapLoading || isLoadingPermissions;
  const isBootstrapLoadingState = isBootstrapLoading;

  return {
    isBootstrapped,
    isLoading: isLoadingState,
    isBootstrapLoading: isBootstrapLoadingState,
    error,
    landingRoute,
    retryBootstrap,
  };
};
